// This composable is used to get package fields for all forms
import type { MappedCartItem } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { useSessionStore } from '@/stores/session'

export const usePackageFields = () => {
	const { activePriceBookVersion } = useSessionStore()

	const {
		softDeletedServiceSubItems,
		nonServiceCategories,
		sectionOverrides,
		findCartItemByCategory,
		getCheckBoxFieldValueFor,
		findIncludedInPkgItemsByCategories,
		addPrices,
		findPackageItem,
	} = useFormFillHelper()

	const packageName = (fieldName: string, packageData?: MappedCartItem) => [
		{
			name: fieldName as string,
			value:
				packageData?.name ?? (findPackageItem() as MappedCartItem)?.name ?? '',
			format: false,
			section: 'header',
		},
	]

	const packagePrice = (fieldName: string, packageData?: MappedCartItem) => {
		const price =
			packageData?.price ?? (findPackageItem() as MappedCartItem)?.price

		return [
			{
				name: fieldName as string,
				value: price || '',
				format: price ? true : false,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'service',
			},
		]
	}

	const packageNetServicePrice = (
		fieldName: string,
		packageData?: MappedCartItem
	) => {
		if (!packageData) {
			packageData = findPackageItem() as MappedCartItem
		}

		const nonServiceSubItems =
			findIncludedInPkgItemsByCategories(nonServiceCategories)
		const nonServiceSubItemsPrice = addPrices(
			nonServiceSubItems,
			false
		) as number
		const softDeletedServiceSubItemsPrice = addPrices(
			softDeletedServiceSubItems,
			false
		) as number

		const total =
			(packageData?.price as number) -
			nonServiceSubItemsPrice -
			softDeletedServiceSubItemsPrice

		return [
			{
				name: fieldName as string,
				value: total === 0 ? 'Incl' : total,
				format: true,
				section: 'service',
			},
		]
	}

	const packageInfo = (packageFields: Record<string, string>) => {
		const packageData = findPackageItem() as MappedCartItem
		const priceField =
			activePriceBookVersion === 2
				? packagePrice(packageFields.price, packageData)
				: packageNetServicePrice(packageFields.price, packageData)

		return [...priceField, ...packageName(packageFields.name, packageData)]
	}

	const packageInfoWithPrepend = (packageFields: Record<string, string>) => {
		const packageData = findPackageItem() as MappedCartItem

		if (!packageData) {
			return []
		}

		if (packageData.price === 0) {
			packageData.price = ''
		}

		packageData.name = `Package: ${packageData.name}`

		return [
			...packagePrice(packageFields.price, packageData),
			...packageName(packageFields.name, packageData),
		]
	}

	const packageInfoWithNetServicePrice = (
		packageFields: Record<string, string>
	) => {
		const packageData = findPackageItem() as MappedCartItem

		return [
			...packageNetServicePrice(packageFields.price, packageData),
			...packageName(packageFields.name, packageData),
		]
	}

	const dispositionCheckBoxFields = (fieldNames: Record<string, string>) => {
		return getCheckBoxFieldValueFor(PbProductType.Disposition, fieldNames)
	}

	const packageIndicator = (fieldName: string) => {
		const packageData = findPackageItem() as MappedCartItem

		return [
			{
				name: fieldName as string,
				value: packageData ? 'See Supplemental For Package' : '',
				format: false,
				section: 'service',
			},
		]
	}

	return {
		packageName,
		packagePrice,
		packageInfo,
		packageInfoWithPrepend,
		packageNetServicePrice,
		packageInfoWithNetServicePrice,
		dispositionCheckBoxFields,
		packageIndicator,
	}
}
